// Statements: the books' own set, from the served catalog. Each catalog row is a tab (a real link to
// its own address); the row's scopes are the only scopes offered. The scope lives in the address, so
// a statement can be linked to. The sealed months lead: with no scope in the address, a statement opens
// on the latest SEALED month. A scope that reaches a month that isn't sealed says so above the
// figures — never smoothed.

import { useMemo, type ReactNode } from 'react';
import { Button, Card, INSET_FOCUS_RING, Select, TAB_LANE, cn } from '@givmo/design-system';
import type { BooksCatalogRow, BooksHome, BooksScope, BooksStatement } from './books-wire';
import type { BooksWords } from './books-words';
import type { StatementQuery } from './books-transport';
import { BooksLoading, BooksPageShell, BooksStateCard, SourceLine } from './books-ui';
import { BooksLink, useBooksNavigate, useBooksSearch } from './books-links';
import { useBooksStatement } from './books-queries';
import { booksRefusal, refusalOf } from './books-state';
import { count, listWords, periodName, spanName } from './books-format';
import { StatementFace, isDrawnForm } from './statement-faces';

const SEALED = 'locked';
const SCOPES: readonly BooksScope[] = [
  'month',
  'fiscal_year',
  'year_to_date',
  'range',
  'as_of',
  'present',
];

export function StatementsPage({
  home,
  words,
  statementId,
}: {
  home: BooksHome;
  words: BooksWords;
  statementId: string | null;
}) {
  const row =
    statementId === null
      ? home.statements[0]
      : home.statements.find((candidate) => candidate.statement === statementId);

  return (
    <BooksPageShell title="Statements" testId="books-statements">
      {home.statements.length > 0 ? (
        <StatementTabs rows={home.statements} current={row?.statement ?? null} />
      ) : null}
      {home.periods.length === 0 ? (
        <BooksStateCard
          testId="books-statements-none"
          title="No statements yet"
          description="These books hold no entries yet, so there’s no statement to show. Statements appear here once the books have a month."
        />
      ) : row === undefined ? (
        <BooksStateCard
          testId="books-statement-not-found"
          title="Books shows no statement by that name for these books"
          description="The statements Books shows for these books are listed above."
        />
      ) : (
        <StatementView home={home} words={words} row={row} />
      )}
    </BooksPageShell>
  );
}

function StatementTabs({ rows, current }: { rows: BooksCatalogRow[]; current: string | null }) {
  return (
    <nav aria-label="Statements" data-testid="books-statement-tabs">
      <ul className={cn('relative flex gap-24 border-b border-border', TAB_LANE)}>
        {rows.map((row) => {
          const selected = row.statement === current;
          return (
            <li key={row.statement} className="relative shrink-0">
              <BooksLink
                view={{ page: 'statements', statement: row.statement }}
                aria-current={selected ? 'page' : undefined}
                className={cn(
                  'relative block whitespace-nowrap rounded-t-md px-4 py-10 font-sans text-s14 font-w500 tracking-normal text-text-tertiary outline-none transition-colors hover:text-text aria-[current=page]:font-w600 aria-[current=page]:text-text-strong',
                  INSET_FOCUS_RING,
                )}
                data-testid={`books-tab-${row.statement}`}
              >
                {row.label}
                {selected ? (
                  <span
                    aria-hidden
                    className="absolute inset-x-0 bottom-0 h-2 rounded-full bg-brand-600"
                  />
                ) : null}
              </BooksLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ── Scope: what the address asks, or the defaults ────────────────────────────

interface Ladder {
  listed: string[];
  status: Map<string, string>;
  latestSealed: string;
  fiscalYears: number[];
  fiscalYearOf: Map<string, number>;
}

function ladderOf(home: BooksHome): Ladder {
  const listed = home.periods.map((period) => period.period);
  const sealed = home.periods.filter((period) => period.status === SEALED);
  return {
    listed,
    status: new Map(home.periods.map((period) => [period.period, period.status])),
    latestSealed: sealed.at(-1)?.period ?? listed.at(-1)!,
    fiscalYears: [...new Set(home.periods.map((period) => period.fiscal_year))].sort(
      (x, y) => y - x,
    ),
    fiscalYearOf: new Map(home.periods.map((period) => [period.period, period.fiscal_year])),
  };
}

function defaultQuery(kind: BooksScope, ladder: Ladder): StatementQuery {
  switch (kind) {
    case 'month':
    case 'as_of':
    case 'year_to_date':
      return { scope: kind, period: ladder.latestSealed };
    case 'fiscal_year':
      return { scope: kind, fiscal_year: ladder.fiscalYearOf.get(ladder.latestSealed)! };
    case 'range':
      return { scope: kind, first_period: ladder.listed[0]!, last_period: ladder.latestSealed };
    case 'present':
      return { scope: kind };
  }
}

function queryFrom(
  read: (name: string) => string | undefined,
  row: BooksCatalogRow,
  ladder: Ladder,
): StatementQuery {
  const asked = read('scope');
  const kind = SCOPES.find((scope) => scope === asked);
  if (kind === undefined || !row.scopes.includes(kind)) return defaultQuery(row.scopes[0]!, ladder);
  const query: StatementQuery = { scope: kind };
  for (const name of ['period', 'first_period', 'last_period'] as const) {
    const value = read(name);
    if (value !== undefined) query[name] = value;
  }
  const fiscalYear = read('fiscal_year');
  if (fiscalYear !== undefined && /^\d{4}$/.test(fiscalYear))
    query.fiscal_year = Number(fiscalYear);
  return query;
}

function StatementView({
  home,
  words,
  row,
}: {
  home: BooksHome;
  words: BooksWords;
  row: BooksCatalogRow;
}) {
  const ladder = useMemo(() => ladderOf(home), [home]);
  const read = useBooksSearch();
  const query = queryFrom(read, row, ladder);
  const statement = useBooksStatement(home.org_ref, row.statement, query);
  const navigate = useBooksNavigate();
  const go = (next: StatementQuery) =>
    navigate({ page: 'statements', statement: row.statement }, { ...next });

  return (
    <div className="flex flex-col gap-16">
      <ScopeBar row={row} query={query} ladder={ladder} words={words} onChange={go} />
      {statement.isPending ? (
        <BooksLoading>Loading the statement…</BooksLoading>
      ) : statement.isError ? (
        <StatementRefusal
          error={statement.error}
          home={home}
          words={words}
          query={query}
          onRetry={() => void statement.refetch()}
        />
      ) : (
        <StatementCard statement={statement.data} ladder={ladder} home={home} words={words} />
      )}
    </div>
  );
}

// ── The scope controls ───────────────────────────────────────────────────────

function ScopeBar({
  row,
  query,
  ladder,
  words,
  onChange,
}: {
  row: BooksCatalogRow;
  query: StatementQuery;
  ladder: Ladder;
  words: BooksWords;
  onChange: (next: StatementQuery) => void;
}) {
  const months = [...ladder.listed]
    .reverse()
    .map((period) => ({ value: period, label: periodName(period) }));
  const years = ladder.fiscalYears.map((year) => ({
    value: String(year),
    label: `Fiscal year ${year}`,
  }));
  const kind = query.scope;
  return (
    <div className="flex flex-wrap items-end gap-12" data-testid="books-scope-bar">
      {row.scopes.length > 1 ? (
        <div
          role="group"
          aria-label="How far the statement reaches"
          className="flex rounded-md border border-border bg-surface p-2"
        >
          {row.scopes.map((scope) => (
            <button
              key={scope}
              type="button"
              aria-pressed={scope === kind}
              onClick={() => (scope === kind ? undefined : onChange(defaultQuery(scope, ladder)))}
              className={cn(
                'h-[32px] rounded-sm px-12 font-sans text-s13 font-w500 tracking-givmo transition-colors',
                scope === kind
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-text-secondary hover:text-text',
              )}
              data-testid={`books-scope-${scope}`}
            >
              {words.scope(scope)}
            </button>
          ))}
        </div>
      ) : null}
      {kind === 'month' || kind === 'year_to_date' || kind === 'as_of' ? (
        <LabeledSelect
          label={
            kind === 'as_of' ? 'As of the end of' : kind === 'year_to_date' ? 'Through' : 'Month'
          }
          value={query.period ?? ''}
          options={months}
          onChange={(period) => onChange({ scope: kind, period })}
          testId="books-scope-period"
        />
      ) : null}
      {kind === 'fiscal_year' ? (
        <LabeledSelect
          label="Fiscal year"
          value={String(query.fiscal_year ?? '')}
          options={years}
          onChange={(year) => onChange({ scope: kind, fiscal_year: Number(year) })}
          testId="books-scope-fiscal-year"
        />
      ) : null}
      {kind === 'range' ? (
        <>
          <LabeledSelect
            label="From"
            value={query.first_period ?? ''}
            options={months}
            onChange={(first) =>
              onChange({ scope: kind, first_period: first, last_period: query.last_period })
            }
            testId="books-scope-first"
          />
          <LabeledSelect
            label="To"
            value={query.last_period ?? ''}
            options={months}
            onChange={(last) =>
              onChange({ scope: kind, first_period: query.first_period, last_period: last })
            }
            testId="books-scope-last"
          />
        </>
      ) : null}
    </div>
  );
}

function LabeledSelect({
  label,
  value,
  options,
  onChange,
  testId,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  testId: string;
}) {
  return (
    <div className="flex items-center gap-8">
      <span className="font-sans text-s13 tracking-givmo text-text-secondary">{label}</span>
      <Select
        appearance="saas"
        aria-label={label}
        value={value}
        options={options}
        onValueChange={(next) => (next ? onChange(next) : undefined)}
        className="min-w-[180px]"
        testId={testId}
      />
    </div>
  );
}

// ── The statement, as folded ─────────────────────────────────────────────────

/**
 * The scope as the ENGINE reports it (a range the books begin inside starts at their first month).
 * A fiscal year is named only as served: with none served, the scope's own served word stands alone,
 * never an empty "Fiscal year  to date".
 */
function scopeWords(statement: BooksStatement, ladder: Ladder, words: BooksWords): string {
  const scope = statement.scope;
  const span = spanName(scope.first_period, scope.last_period);
  const booksBegin = ladder.listed[0];
  const beginsInside =
    (scope.kind === 'fiscal_year' || scope.kind === 'year_to_date') &&
    scope.first_period === booksBegin;
  const beginNote =
    beginsInside && booksBegin ? `The books begin in ${periodName(booksBegin)}.` : undefined;
  switch (scope.kind) {
    case 'month':
      return periodName(scope.last_period ?? '');
    case 'as_of':
      return `As of the end of ${periodName(scope.last_period ?? '')}`;
    case 'fiscal_year':
      return [
        scope.fiscal_year != null ? `Fiscal year ${scope.fiscal_year}` : words.scope(scope.kind),
        span,
        beginNote,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'year_to_date':
      return [
        scope.fiscal_year != null
          ? `Fiscal year ${scope.fiscal_year} to date`
          : words.scope(scope.kind),
        span,
        beginNote,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'range':
      return span;
    case 'present':
      return `Everything posted through ${periodName(ladder.listed.at(-1) ?? '')}`;
  }
}

/** The months a statement reaches that are not sealed, named with their status (plan §3.2). */
function UnsealedNote({
  statement,
  ladder,
  words,
}: {
  statement: BooksStatement;
  ladder: Ladder;
  words: BooksWords;
}): ReactNode {
  const scope = statement.scope;
  const last = scope.last_period ?? ladder.listed.at(-1) ?? '';
  const first =
    scope.kind === 'as_of' || scope.kind === 'present'
      ? (ladder.listed[0] ?? '')
      : (scope.first_period ?? last);
  const reached = ladder.listed.filter((period) => period >= first && period <= last);
  const unsealed = reached.filter((period) => ladder.status.get(period) !== SEALED).reverse();
  const pastBooks = last > (ladder.listed.at(-1) ?? last);
  if (unsealed.length === 0 && !pastBooks) return null;
  const named = unsealed.map(
    (period) =>
      `${periodName(period)} (${words.status(ladder.status.get(period) ?? '').label.toLowerCase()})`,
  );
  const sealedWord = words.status(SEALED).label.toLowerCase();
  return (
    <div className="px-20 pt-16">
      <div
        role="note"
        className="flex flex-col gap-2 border-l-2 border-warning-500 bg-warning-50 px-16 py-10"
        data-testid="books-statement-unsealed"
      >
        {unsealed.length > 0 ? (
          <p className="font-sans text-s13 tracking-givmo text-text">
            Includes {listWords(named)}, {unsealed.length === 1 ? 'which isn’t' : 'which aren’t'}{' '}
            {sealedWord}. These figures can still change.
          </p>
        ) : null}
        {pastBooks ? (
          <p className="font-sans text-s13 tracking-givmo text-text">
            The books run through {periodName(ladder.listed.at(-1) ?? '')}; the rest of{' '}
            {scope.kind === 'range' ? 'this range' : 'this fiscal year'} has no entries yet.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function StatementCard({
  statement,
  ladder,
  home,
  words,
}: {
  statement: BooksStatement;
  ladder: Ladder;
  home: BooksHome;
  words: BooksWords;
}) {
  const body = statement.body;
  return (
    <div className="flex flex-col gap-8">
      <Card testId={`books-statement-${statement.statement}`}>
        <div className="flex flex-col gap-2 border-b border-border-subtle px-20 py-16">
          <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
            {statement.label}
          </h2>
          <p className="font-sans text-s13 tracking-givmo text-text-secondary">
            {home.identity.legal_name} · {scopeWords(statement, ladder, words)}
          </p>
        </div>
        <UnsealedNote statement={statement} ladder={ladder} words={words} />
        <div className="px-4 py-8">
          {isDrawnForm(body) ? (
            <StatementFace body={body} words={words} />
          ) : (
            <p
              className="px-16 py-12 font-sans text-s14 tracking-givmo text-text-secondary"
              data-testid="books-statement-not-drawn"
            >
              This statement can’t be shown here yet.
            </p>
          )}
        </div>
      </Card>
      {statement.source.chain_position != null ? (
        <SourceLine>
          As the books stood after entry {count(statement.source.chain_position)}.
        </SourceLine>
      ) : null}
    </div>
  );
}

// ── The typed refusals, said plainly ─────────────────────────────────────────

function StatementRefusal({
  error,
  home,
  words,
  query,
  onRetry,
}: {
  error: unknown;
  home: BooksHome;
  words: BooksWords;
  query: StatementQuery;
  onRetry: () => void;
}) {
  const { code, context, detail } = refusalOf(error);
  const first = home.periods[0];
  const last = home.periods.at(-1)?.period;
  if (code === 'BOOKS_NO_ENTRIES_IN_RANGE') {
    const asked =
      query.scope === 'fiscal_year'
        ? `fiscal year ${query.fiscal_year}`
        : query.scope === 'range'
          ? spanName(query.first_period, query.last_period)
          : periodName(query.period ?? '');
    // Only what the period list knows: a listed month's status, or where the listed months begin
    // and end. The page never claims which months hold entries (the list doesn't say).
    const listedStatus = query.period
      ? home.periods.find((period) => period.period === query.period)?.status
      : undefined;
    const before =
      first !== undefined &&
      ((query.period !== undefined && query.period < first.period) ||
        (query.last_period !== undefined && query.last_period < first.period) ||
        (query.fiscal_year !== undefined && query.fiscal_year < first.fiscal_year));
    const description = listedStatus
      ? `${periodName(query.period!)} is ${words.status(listedStatus).label.toLowerCase()}, and nothing has been posted to it yet.`
      : before && first
        ? `These books begin in ${periodName(first.period)}.`
        : last
          ? `The latest month in these books is ${periodName(last)}.`
          : undefined;
    return (
      <BooksStateCard
        testId="books-statement-no-entries"
        title={`No entries in ${asked}`}
        description={description}
      />
    );
  }
  if (code === 'BOOKS_STATEMENT_WITHHELD') {
    const reason = typeof context?.reason === 'string' ? context.reason : '';
    const withheld = words.withheld(reason);
    return (
      <BooksStateCard
        testId="books-statement-withheld"
        title={withheld.label}
        description={withheld.meaning || detail || undefined}
      />
    );
  }
  if (code === 'BOOKS_STATEMENT_NOT_PRODUCED' || code === 'BOOKS_STATEMENT_NOT_FOUND') {
    return (
      <BooksStateCard
        testId="books-statement-not-produced"
        title="Books shows no statement by that name for these books"
        description="The statements Books shows for these books are listed above."
      />
    );
  }
  if (code === 'BOOKS_SCOPE_INVALID') {
    return (
      <BooksStateCard
        testId="books-statement-scope-invalid"
        title="This statement isn’t read that way"
        description={detail ?? undefined}
      />
    );
  }
  const temporary = booksRefusal(error) === 'temporary';
  return (
    <BooksStateCard
      testId="books-statement-unavailable"
      title="This statement can’t be read right now"
      description={temporary ? 'Try again in a few minutes.' : undefined}
      action={
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={onRetry}
          testId="books-statement-retry"
        >
          Try again
        </Button>
      }
    />
  );
}
